import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  FileText,
  FileSpreadsheet,
  Edit2,
  Trash2,
  Play,
  Pause,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  PlayCircle,
  Users
} from 'lucide-react'
import { Card } from '../ui'
import Modal from '../ui/Modal'
import RecipientPicker from './RecipientPicker'
import useReportStore from '../../stores/reportStore'
import { ScheduledReport, ScheduleStatus, RecipientReference } from '../../types/report'
import { useToast } from '../../contexts/ToastContext'
import { useReportTracking } from '../../contexts/AnalyticsContext'

interface ScheduledReportsTabProps {
  customerId: string | null
  onEditSchedule: (schedule: ScheduledReport) => void
}

const ScheduledReportsTab: React.FC<ScheduledReportsTabProps> = ({
  customerId,
  onEditSchedule
}) => {
  const { showSuccess, showError } = useToast()
  const { trackScheduleStatusToggled, trackScheduleDeleted, trackScheduleRunNow } = useReportTracking()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [runningNowId, setRunningNowId] = useState<string | null>(null)
  const [editRecipientsSchedule, setEditRecipientsSchedule] = useState<ScheduledReport | null>(null)
  const [editRecipientRefs, setEditRecipientRefs] = useState<RecipientReference[]>([])
  const [isSavingRecipients, setIsSavingRecipients] = useState(false)

  const {
    scheduledReports,
    scheduledLoading,
    scheduledError,
    scheduledPagination,
    loadScheduledReports,
    deleteScheduledReport,
    toggleScheduleStatus,
    runScheduleNow,
    updateScheduleRecipients
  } = useReportStore()

  // Load scheduled reports when customer changes
  useEffect(() => {
    if (customerId) {
      loadScheduledReports(customerId)
    }
  }, [customerId, loadScheduledReports])

  const handleRefresh = () => {
    if (customerId) {
      loadScheduledReports(customerId)
    }
  }

  const handleToggleStatus = async (scheduleId: string) => {
    setTogglingId(scheduleId)
    try {
      const schedule = scheduledReports.find((s) => s.id === scheduleId)
      const newStatus = schedule?.status === 'active' ? 'paused' : 'active'
      await toggleScheduleStatus(scheduleId)
      trackScheduleStatusToggled(scheduleId, newStatus, customerId || undefined)
      showSuccess(
        `Schedule ${newStatus}`,
        `The scheduled report has been ${newStatus}.`
      )
    } catch (error) {
      showError(
        'Action failed',
        error instanceof Error ? error.message : 'Failed to update schedule'
      )
    } finally {
      setTogglingId(null)
    }
  }

  const handleDelete = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to delete this scheduled report?')) {
      return
    }

    const schedule = scheduledReports.find((s) => s.id === scheduleId)
    setDeletingId(scheduleId)
    try {
      await deleteScheduledReport(scheduleId)
      trackScheduleDeleted(scheduleId, schedule?.reportType || '', customerId || undefined)
      showSuccess('Schedule deleted', 'The scheduled report has been removed.')
    } catch (error) {
      showError(
        'Delete failed',
        error instanceof Error ? error.message : 'Failed to delete schedule'
      )
    } finally {
      setDeletingId(null)
    }
  }

  const handleRunNow = async (schedule: ScheduledReport) => {
    const hasRecipients =
      (schedule.emailConfig.recipients && schedule.emailConfig.recipients.length > 0) ||
      (schedule.emailConfig.recipientRefs && schedule.emailConfig.recipientRefs.length > 0)

    if (!schedule.emailConfig.enabled || !hasRecipients) {
      showError(
        'Cannot run now',
        'Email recipients must be configured to run the report immediately.'
      )
      return
    }

    setRunningNowId(schedule.id)
    try {
      await runScheduleNow(schedule.id)
      trackScheduleRunNow(schedule.id, schedule.reportType, customerId || undefined)
      showSuccess(
        'Report sent',
        'The report has been generated and sent to the configured recipients.'
      )
    } catch (error) {
      showError(
        'Failed to run report',
        error instanceof Error ? error.message : 'Please try again later.'
      )
    } finally {
      setRunningNowId(null)
    }
  }

  const handlePageChange = (page: number) => {
    if (customerId) {
      loadScheduledReports(customerId, page)
    }
  }

  const getStatusBadge = (status: ScheduleStatus) => {
    const statusConfig = {
      active: {
        icon: CheckCircle,
        bg: 'bg-green-100',
        text: 'text-green-700',
        border: 'border-green-200',
        label: 'Active'
      },
      paused: {
        icon: Pause,
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        border: 'border-yellow-200',
        label: 'Paused'
      },
      completed: {
        icon: CheckCircle,
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        border: 'border-blue-200',
        label: 'Completed'
      },
      cancelled: {
        icon: XCircle,
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        border: 'border-gray-200',
        label: 'Cancelled'
      }
    }

    const config = statusConfig[status]
    const Icon = config.icon

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}
      >
        <Icon className='h-3 w-3 mr-1' />
        {config.label}
      </span>
    )
  }

  const getFormatIcon = (format: 'pdf' | 'csv') => {
    return format === 'pdf' ? (
      <div className='p-2 bg-red-100 rounded-lg'>
        <FileText className='h-4 w-4 text-red-600' />
      </div>
    ) : (
      <div className='p-2 bg-green-100 rounded-lg'>
        <FileSpreadsheet className='h-4 w-4 text-green-600' />
      </div>
    )
  }

  const formatScheduleDate = (dateString?: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatSchedule = (schedule: ScheduledReport['schedule']) => {
    if (schedule.type === 'one-time') {
      const formattedDate = formatScheduleDate(schedule.oneTimeDate)
      return `One-time: ${formattedDate} at ${schedule.oneTimeTime}`
    }
    if (schedule.type === 'daily') {
      return `Daily at ${schedule.recurringTime}`
    }
    if (schedule.type === 'weekly') {
      const days = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday'
      ]
      return `Weekly: ${days[schedule.recurringDay || 0]} at ${schedule.recurringTime}`
    }
    if (schedule.type === 'monthly') {
      const suffix = getOrdinalSuffix(schedule.recurringDay || 1)
      return `Monthly: ${schedule.recurringDay}${suffix} at ${schedule.recurringTime}`
    }
    return 'Unknown schedule'
  }

  const getOrdinalSuffix = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return s[(v - 20) % 10] || s[v] || s[0]
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  // Empty state - no customer selected
  if (!customerId) {
    return (
      <Card className='p-8'>
        <div className='text-center'>
          <Clock className='h-12 w-12 text-gray-400 mx-auto mb-4' />
          <h3 className='text-lg font-semibold text-gray-900 mb-2'>
            Select a Customer
          </h3>
          <p className='text-sm text-gray-600'>
            Please select a customer to view scheduled reports
          </p>
        </div>
      </Card>
    )
  }

  if (scheduledError) {
    return (
      <Card className='p-8'>
        <div className='text-center'>
          <XCircle className='h-12 w-12 text-red-400 mx-auto mb-4' />
          <h3 className='text-lg font-semibold text-gray-900 mb-2'>
            Error Loading Schedules
          </h3>
          <p className='text-sm text-gray-600 mb-4'>{scheduledError}</p>
          <button
            onClick={handleRefresh}
            className='inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700'
          >
            <RefreshCw className='h-3.5 w-3.5 mr-1.5' />
            Try Again
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card className='overflow-hidden relative'>
      {/* Loading Overlay */}
      {scheduledLoading && (
        <div className='absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10'>
          <div className='flex items-center space-x-3'>
            <Loader2 className='h-6 w-6 text-blue-600 animate-spin' />
            <span className='text-gray-900'>Loading scheduled reports...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className='px-6 py-4 border-b border-gray-200 flex items-center justify-between'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900'>
            Scheduled Reports
          </h3>
          <p className='text-xs text-gray-500 mt-0.5'>
            {scheduledPagination.totalScheduledReports} schedules configured
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={scheduledLoading}
          className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50'
          title='Refresh'
        >
          <RefreshCw
            className={`h-4 w-4 ${scheduledLoading ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      {/* Table */}
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead className='bg-gray-50 border-b border-gray-200'>
            <tr>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Report
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Report Name
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Schedule
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Status
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Last Run
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Next Run
              </th>
              <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {scheduledReports.map((schedule, index) => (
              <motion.tr
                key={schedule.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className='hover:bg-gray-50 transition-colors'
              >
                <td className='px-6 py-4 whitespace-nowrap'>
                  <div className='flex items-center space-x-2'>
                    {getFormatIcon(schedule.format)}
                    <p className='text-xs font-medium text-gray-900'>
                      {schedule.reportTitle}
                    </p>
                  </div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <p className='text-xs text-gray-900'>
                    {schedule.reportName || (
                      <span className='text-gray-400 italic'>-</span>
                    )}
                  </p>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <div className='flex items-center space-x-2'>
                    <Clock className='h-3.5 w-3.5 text-gray-400' />
                    <span className='text-xs text-gray-900'>
                      {formatSchedule(schedule.schedule)}
                    </span>
                  </div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  {getStatusBadge(schedule.status)}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <p className='text-xs text-gray-900'>
                    {formatDate(schedule.lastRunAt)}
                  </p>
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <p className='text-xs text-gray-900'>
                    {formatDate(schedule.nextRunAt)}
                  </p>
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-right'>
                  <div className='flex items-center justify-end space-x-1'>
                    {/* Toggle Active/Paused */}
                    {(schedule.status === 'active' ||
                      schedule.status === 'paused') && (
                      <button
                        onClick={() => handleToggleStatus(schedule.id)}
                        disabled={togglingId === schedule.id}
                        className={`p-2 rounded-lg transition-colors ${
                          schedule.status === 'active'
                            ? 'text-yellow-600 hover:bg-yellow-50'
                            : 'text-green-600 hover:bg-green-50'
                        } disabled:opacity-50`}
                        title={
                          schedule.status === 'active'
                            ? 'Pause schedule'
                            : 'Resume schedule'
                        }
                      >
                        {togglingId === schedule.id ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : schedule.status === 'active' ? (
                          <Pause className='h-4 w-4' />
                        ) : (
                          <Play className='h-4 w-4' />
                        )}
                      </button>
                    )}

                    {/* Run Now */}
                    {schedule.status === 'active' && (
                      <button
                        onClick={() => handleRunNow(schedule)}
                        disabled={runningNowId === schedule.id}
                        className='p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50'
                        title='Run now'
                      >
                        {runningNowId === schedule.id ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <PlayCircle className='h-4 w-4' />
                        )}
                      </button>
                    )}

                    {/* Edit Recipients */}
                    <button
                      onClick={() => {
                        setEditRecipientsSchedule(schedule)
                        setEditRecipientRefs(schedule.emailConfig.recipientRefs || [])
                      }}
                      className='p-2 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors'
                      title='Edit recipients'
                    >
                      <Users className='h-4 w-4' />
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => onEditSchedule(schedule)}
                      className='p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors'
                      title='Edit schedule'
                    >
                      <Edit2 className='h-4 w-4' />
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(schedule.id)}
                      disabled={deletingId === schedule.id}
                      className='p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50'
                      title='Delete schedule'
                    >
                      {deletingId === schedule.id ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <Trash2 className='h-4 w-4' />
                      )}
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {!scheduledLoading && scheduledReports.length === 0 && (
        <div className='text-center py-12'>
          <Clock className='mx-auto h-12 w-12 text-gray-400' />
          <h3 className='mt-2 text-sm font-medium text-gray-900'>
            No scheduled reports
          </h3>
          <p className='mt-1 text-sm text-gray-500'>
            Schedule a report from the Available Reports tab
          </p>
        </div>
      )}

      {/* Edit Recipients Modal */}
      <Modal
        isOpen={!!editRecipientsSchedule}
        onClose={() => {
          setEditRecipientsSchedule(null)
          setEditRecipientRefs([])
        }}
        title={`Edit Recipients: ${editRecipientsSchedule?.reportName || editRecipientsSchedule?.reportTitle || ''}`}
        size='md'
      >
        <div className='space-y-4'>
          <RecipientPicker
            selectedRefs={editRecipientRefs}
            onChange={setEditRecipientRefs}
            activationId={editRecipientsSchedule?.parameters?.activationId as string | undefined}
            disabled={isSavingRecipients}
          />

          <div className='flex items-center space-x-3 pt-4 border-t border-gray-200'>
            <button
              onClick={() => {
                setEditRecipientsSchedule(null)
                setEditRecipientRefs([])
              }}
              disabled={isSavingRecipients}
              className='flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!editRecipientsSchedule) return
                setIsSavingRecipients(true)
                try {
                  await updateScheduleRecipients(editRecipientsSchedule.id, editRecipientRefs)
                  showSuccess('Recipients updated', 'The recipients have been saved.')
                  setEditRecipientsSchedule(null)
                  setEditRecipientRefs([])
                } catch (error) {
                  showError(
                    'Failed to update recipients',
                    error instanceof Error ? error.message : 'Please try again later.'
                  )
                } finally {
                  setIsSavingRecipients(false)
                }
              }}
              disabled={isSavingRecipients || editRecipientRefs.length === 0}
              className='flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isSavingRecipients ? (
                <>
                  <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Recipients'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Pagination */}
      {scheduledPagination.totalPages > 1 && (
        <div className='bg-white px-4 py-3 border-t border-gray-200 sm:px-6'>
          <div className='flex items-center justify-between'>
            <div className='text-xs text-gray-700'>
              Page{' '}
              <span className='font-medium'>
                {scheduledPagination.currentPage}
              </span>{' '}
              of{' '}
              <span className='font-medium'>
                {scheduledPagination.totalPages}
              </span>
            </div>
            <div className='flex space-x-2'>
              <button
                onClick={() =>
                  handlePageChange(scheduledPagination.currentPage - 1)
                }
                disabled={scheduledPagination.currentPage === 1}
                className='px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Previous
              </button>
              <button
                onClick={() =>
                  handlePageChange(scheduledPagination.currentPage + 1)
                }
                disabled={
                  scheduledPagination.currentPage ===
                  scheduledPagination.totalPages
                }
                className='px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

export default ScheduledReportsTab
